import { useState, useEffect, useCallback } from "react";
import { History, Trash2, Plus, FileDown, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const EMPTY = () => ({ action: "wymiana", old_serial: "", new_serial: "", note: "", event_date: new Date().toISOString().slice(0, 10) });

export default function DeviceHistory({ deviceType, deviceId, role }) {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY());

  const load = useCallback(() => {
    api.get(`/device-history?device_type=${deviceType}&device_id=${deviceId}`)
      .then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [deviceType, deviceId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try {
      await api.post("/device-history", { device_type: deviceType, device_id: deviceId, ...form });
      toast.success(t("Zapisano wpis historii"));
      setOpen(false); setForm(EMPTY()); load();
    } catch { toast.error(t("Błąd zapisu wpisu historii")); }
  };
  const del = async (id) => {
    if (!window.confirm(t("Usunąć wpis historii?"))) return;
    try { await api.delete(`/device-history/${id}`); load(); } catch { /* noop */ }
  };
  const exportFile = async (fmt) => {
    try {
      const r = await api.get(`/device-history/export?device_type=${deviceType}&device_id=${deviceId}&format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `historia_${deviceType}_${deviceId}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error(t("Błąd eksportu historii")); }
  };

  return (
    <div className="p-4 border-b border-slate-800" data-testid="device-history-block">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1.5"><History size={12} /> {t("Historia montażu i wymian")} ({rows.length})</div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => exportFile("csv")} data-testid="device-history-export-csv" title={t("Eksport CSV")}
            className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 py-1 flex items-center gap-1"><FileText size={12} /> CSV</button>
          <button onClick={() => exportFile("pdf")} data-testid="device-history-export-pdf" title={t("Eksport PDF")}
            className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 py-1 flex items-center gap-1"><FileDown size={12} /> PDF</button>
          {role !== "odczyt" && (
            <button onClick={() => setOpen((v) => !v)} data-testid="device-history-add-btn"
              className="text-xs bg-cyan-700 hover:bg-cyan-600 rounded px-2 py-1 flex items-center gap-1"><Plus size={12} /> {t("Dodaj wymianę")}</button>
          )}
        </div>
      </div>
      {open && (
        <div className="mb-3 space-y-2 rounded-md border border-slate-800 bg-slate-800/40 p-2.5" data-testid="device-history-form">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] text-slate-500 uppercase">{t("Data")}
              <input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                data-testid="device-history-date" className="w-full mt-0.5 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200" /></label>
            <label className="text-[10px] text-slate-500 uppercase">{t("Typ zdarzenia")}
              <select value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })}
                data-testid="device-history-action" className="w-full mt-0.5 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200">
                <option value="montaż">{t("Montaż / instalacja")}</option>
                <option value="wymiana">{t("Wymiana sprzętu")}</option>
                <option value="serwis">{t("Serwis")}</option>
                <option value="demontaż">{t("Demontaż")}</option>
              </select></label>
            <label className="text-[10px] text-slate-500 uppercase">{t("Stary S/N")}
              <input value={form.old_serial} onChange={(e) => setForm({ ...form, old_serial: e.target.value })}
                data-testid="device-history-old-serial" className="w-full mt-0.5 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200" /></label>
            <label className="text-[10px] text-slate-500 uppercase">{t("Nowy S/N")}
              <input value={form.new_serial} onChange={(e) => setForm({ ...form, new_serial: e.target.value })}
                data-testid="device-history-new-serial" className="w-full mt-0.5 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200" /></label>
          </div>
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("Uwagi")}
            data-testid="device-history-note" className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200" />
          <div className="flex gap-2">
            <button onClick={add} data-testid="device-history-save" className="flex-1 bg-cyan-700 hover:bg-cyan-600 rounded py-1.5 text-xs">{t("Zapisz")}</button>
            <button onClick={() => setOpen(false)} className="px-3 bg-slate-700 hover:bg-slate-600 rounded py-1.5 text-xs">{t("Anuluj")}</button>
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500">{t("Brak wpisów historii")}</div>
      ) : (
        <div className="space-y-1.5" data-testid="device-history-list">
          {rows.map((r) => (
            <div key={r.id} data-testid={`device-history-item-${r.id}`} className="flex items-start gap-2 rounded-md border border-slate-800 bg-slate-800/40 px-2.5 py-1.5">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-200"><b>{String(r.event_date || "").slice(0, 10)}</b> · {r.action}</div>
                {(r.old_serial || r.new_serial) && <div className="text-[11px] text-slate-400">{r.old_serial || "—"} → {r.new_serial || "—"}</div>}
                {r.note && <div className="text-[11px] text-slate-500">{r.note}</div>}
                {r.by && <div className="text-[10px] text-slate-600">{t("przez")}: {r.by}</div>}
              </div>
              {role !== "odczyt" && (
                <button onClick={() => del(r.id)} data-testid={`device-history-del-${r.id}`} className="p-1 rounded hover:bg-red-600/30 text-red-400 shrink-0"><Trash2 size={13} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
